import {
  applyReaction,
  sortByCreatedAt,
  upsertMessageById,
} from './internalChatMessageHelpers';

export const inMessageWindow = (message, records, meta) => {
  if (!records.length || records.some(item => item.id === message.id))
    return true;
  if (sortByCreatedAt(message, records[0]) < 0) return false;
  return !meta?.has_more_after || sortByCreatedAt(message, records.at(-1)) <= 0;
};

export const applyWindowEvents = (records, events, meta) =>
  events.reduce((items, event) => {
    if (!event.message)
      return items.map(message => applyReaction(message, event));
    return inMessageWindow(event.message, items, meta)
      ? upsertMessageById(items, event.message).sort(sortByCreatedAt)
      : items;
  }, records);

export const windowEdges = (records, meta) =>
  meta.context_id && records.length
    ? {
        ...meta,
        before: records[0].created_at,
        before_id: records[0].id,
        after: records.at(-1).created_at,
        after_id: records.at(-1).id,
      }
    : meta;

export const mergeWindowMeta = (previous, incoming, newer = false) => {
  if (newer)
    return {
      ...previous,
      after: incoming.after,
      after_id: incoming.after_id,
      has_more_after: incoming.has_more,
    };
  return {
    ...incoming,
    after: previous.after,
    after_id: previous.after_id,
    ...(previous.context_id
      ? {
          context_id: previous.context_id,
          has_more_after: previous.has_more_after,
        }
      : {}),
  };
};

export const revalidateMessageWindow = async ({
  records,
  meta,
  getPage,
  getMessage,
  field,
  current,
}) => {
  const latest = await getPage(
    meta.has_more_after ? { around_id: meta.context_id } : undefined
  );
  if (!current()) return null;
  const edge = meta.has_more_after ? records.at(-1) : latest[field].at(-1);
  const first = await getMessage(records[0].id);
  if (!current()) return null;
  const collected = [first];
  let cursor = first;
  while (edge && sortByCreatedAt(cursor, edge) < 0) {
    // Every page depends on the previous tuple; parallel requests leave gaps.
    // eslint-disable-next-line no-await-in-loop
    const page = await getPage({
      after: cursor.created_at,
      after_id: cursor.id,
    });
    if (!current()) return null;
    const next = page[field].at(-1);
    if (!next || sortByCreatedAt(next, cursor) <= 0)
      throw new Error('Message cursor failed to advance');
    collected.push(
      ...page[field].filter(item => sortByCreatedAt(item, edge) <= 0)
    );
    cursor = next;
  }
  const last = collected.at(-1);
  const older = await getPage({
    before: first.created_at,
    before_id: first.id,
    limit: 1,
  });
  if (!current()) return null;
  const newer = await getPage({
    after: last.created_at,
    after_id: last.id,
    limit: 1,
  });
  if (!current()) return null;
  return {
    ...latest,
    [field]: collected,
    meta: {
      ...meta,
      before: first.created_at,
      before_id: first.id,
      after: last.created_at,
      after_id: last.id,
      has_more: Boolean(older[field].length),
      has_more_after: Boolean(newer[field].length),
    },
  };
};
